import { ReactNode, useEffect, useLayoutEffect, useMemo, useState } from "react"
import { init as swiftInit } from '../.build/plugins/PackageToJS/outputs/Package/index.js'
import { useProgram } from "./hooks/useProgram.ts"
import { MouseHandler } from "./MouseHandler.tsx"
import sceneSource from './scene.glsl?raw'
import colorsSource from './scenes/colors.glsl?raw'
import cutBoxesSource from './scenes/cutBoxes.glsl?raw'
import cutSpheresSource from './scenes/cutSpheres.glsl?raw'
import cutToriSource from './scenes/cutTori.glsl?raw'
import starSource from './scenes/star.glsl?raw'
import towerSource from './scenes/tower.glsl?raw'
import fragmentSource from './shader.fs?raw'
import vertexSource from './shader.vs?raw'
import { mat4, vec3 } from "./VectorMath.ts"

const { exports: swiftExports } = await swiftInit({
  imports: {}
})

const scenes: Map<string, string> = new Map([
  ['Colors', colorsSource],
  ['Cut Boxes', cutBoxesSource],
  ['Cut Spheres', cutSpheresSource],
  ['Cut Tori', cutToriSource],
  ['Star', starSource],
  ['Tower', towerSource],
  ['Ad-Hoc', sceneSource],
  ['Embedded', swiftExports.fragmentShaderFragment()]
])

// To redraw after a hot reload, I need to detect the hot reload.
const viteHotApi = import.meta.hot
if (viteHotApi) {
  const data = viteHotApi.data
  if (data.hotReloadCount === undefined) {
    data.hotReloadCount = 0
  }
  viteHotApi.accept(() => {
    data.hotReloadCount += 1
    console.log(`import.meta.hot.accept: ${data.hotReloadCount}`)
  })
}

export function AppView() {
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null)
  const [sceneName, setSceneName] = useState('Star')
  const [u_tMax, set_u_tMax] = useState(100.0)
  const [u_iMax, set_u_iMax] = useState(256)
  const [u_softShadow, set_u_softShadow] = useState(0.5)
  const [u_ambientOcclusion, set_u_ambientOcclusion] = useState(0.005)
  const [azimuth, setAzimuth] = useState(0.0)
  const [altitude, setAltitude] = useState(0.0)
  const [cameraDistance, setCameraDistance] = useState(10.0)

  const gl = useMemo(() => {
    return canvas?.getContext('webgl2', {
      alpha: false,
      depth: false,
    })
  }, [canvas])

  return <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
    }}>
    <div style={{
      padding: '8px',
      display: 'grid',
      gridTemplateColumns: '0fr 1fr',
      gap: '8px',
    }}>
      <span style={{ justifySelf: 'end' }}>Distance&nbsp;limit:</span>
      <input type='range' value={u_tMax} onChange={event => set_u_tMax(event.target.valueAsNumber)} min={0} max={1000} step='any' />
      <span style={{ justifySelf: 'end' }}>Iteration&nbsp;limit:</span>
      <input type='range' value={u_iMax} onChange={event => set_u_iMax(event.target.valueAsNumber)} min={0} max={1000} step={1} />
      <span style={{ justifySelf: 'end' }}>Shadow&nbsp;softness:</span>
      <input type='range' value={u_softShadow} onChange={event => set_u_softShadow(event.target.valueAsNumber)} min={0} max={1} step='any' />
      <span style={{ justifySelf: 'end' }}>Ambient&nbsp;occlusion:</span>
      <input type='range' value={u_ambientOcclusion} onChange={event => set_u_ambientOcclusion(event.target.valueAsNumber)} min={0} max={0.03} step='any' />
      <span style={{ justifySelf: 'end' }}>Scene:</span>
      <ScenePicker value={sceneName} setValue={setSceneName} />
    </div>
    <canvas
      ref={setCanvas}
      style={{
        overflow: 'auto',
        flexGrow: 1,
      }}
    >
      {canvas && <MouseHandler
        element={canvas}
        setAzimuth={setAzimuth}
        setAltitude={setAltitude}
        setCameraDistance={setCameraDistance}
      />}

      {gl && <Renderer
        gl={gl}
        hmrCount={viteHotApi?.data?.hotReloadCount ?? 0}
        sceneSource={scenes.get(sceneName)!}
        u_tMax={u_tMax}
        u_iMax={u_iMax}
        u_softShadow={u_softShadow}
        u_ambientOcclusion={u_ambientOcclusion}
        azimuth={azimuth}
        altitude={altitude}
        cameraDistance={cameraDistance}
      />}
    </canvas>
  </div>
}

function ScenePicker(props: {
  value: string,
  setValue: (value: string) => void,
}) {
  const { value, setValue } = props

  return <select
    value={value}
    onChange={event => setValue(event.target.value)}
    style={{
      justifySelf: 'start',
    }}
  >
    {scenes.keys().map(key => <option key={key} value={key}>{key}</option>)}
  </select>
}

function Renderer(props: {
  gl: WebGL2RenderingContext,
  hmrCount: number,
  sceneSource: string,
  u_tMax: number,
  u_iMax: number,
  u_softShadow: number,
  u_ambientOcclusion: number,
  azimuth: number,
  altitude: number,
  cameraDistance: number,
}): ReactNode {
  const {
    gl, hmrCount,
    sceneSource,
    u_tMax, u_iMax,
    u_softShadow, u_ambientOcclusion,
    azimuth, altitude, cameraDistance
  } = props
  const canvas = gl.canvas as HTMLCanvasElement

  const [resizeCount, setResizeCount] = useState(0)

  useLayoutEffect(() => {
    if (!canvas) {
      return
    }

    const observer = new ResizeObserver(() => {
      setResizeCount(count => count + 1)
    })

    observer.observe(canvas)

    return () => observer.disconnect()
  }, [canvas])

  const includes = useMemo(() => new Map<string, string>([
    ['sdfShader', sceneSource],
  ]), [sceneSource])

  const program = useProgram('shader', gl, vertexSource, fragmentSource, includes)

  const locations = useMemo(() => ({
    u_canvasSize: gl.getUniformLocation(program, 'u_canvasSize'),
    u_fov: gl.getUniformLocation(program, 'u_fov'),
    u_tMax: gl.getUniformLocation(program, 'u_tMax'),
    u_iMax: gl.getUniformLocation(program, 'u_iMax'),
    u_softShadow: gl.getUniformLocation(program, 'u_softShadow'),
    u_ambientOcclusion: gl.getUniformLocation(program, 'u_ambientOcclusion'),
    u_worldFromView: gl.getUniformLocation(program, 'u_worldFromView'),
  }), [gl, program])

  useEffect(() => {
    // Make the canvas's logical size match its visual size, so that it is drawn at full resolution and with the correct aspect ratio.
    // https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/By_example/Canvas_size_and_WebGL

    // This needs to be done immediately before redrawing the canvas to minimize flicker, because changing canvas.width or canvas.height clears the canvas.

    const scale = window.devicePixelRatio
    const desiredWidth = canvas.clientWidth * scale
    const desiredHeight = canvas.clientHeight * scale

    if (canvas.width !== desiredWidth || canvas.height !== desiredHeight) {
      canvas.width = desiredWidth
      canvas.height = desiredHeight
      gl.viewport(0, 0, canvas.width, canvas.height)
    }

    gl.clearColor(1, 0, 0, 1)
    gl.clear(gl.COLOR_BUFFER_BIT)

    gl.useProgram(program)
    gl.uniform2f(locations.u_canvasSize, canvas.width, canvas.height)
    gl.uniform1f(locations.u_tMax, u_tMax)
    gl.uniform1f(locations.u_iMax, u_iMax)
    gl.uniform1f(locations.u_fov, 0.5 * Math.PI / 4)
    const u = Math.pow(1000.0, 1.0 - u_softShadow)
    gl.uniform1f(locations.u_softShadow, u)
    gl.uniform1f(locations.u_ambientOcclusion, u_ambientOcclusion)
    const viewFromWorld = makeWorldFromView({ azimuth, altitude, cameraDistance })
    gl.uniformMatrix4fv(locations.u_worldFromView, false, Float32Array.from(viewFromWorld))

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

  }, [
    canvas, gl, program, locations, resizeCount,
    hmrCount, u_tMax, u_iMax, u_softShadow, u_ambientOcclusion,
    azimuth, altitude, cameraDistance,
  ])

  return <></>
}

function makeWorldFromView(props: {
  azimuth: number,
  altitude: number,
  cameraDistance: number,
}): mat4 {
  const { azimuth, altitude, cameraDistance } = props

  return mat4.identity
    .translated(new vec3(0, 0, cameraDistance))
    .rotated(vec3.xPlus, altitude)
    .rotated(vec3.yPlus, azimuth)
}
